// Spec: specs/account/billing-info/functional_requirements.md §5
//       specs/payments/prepay/functional_requirements.md §6.5

// The provider paints its fields inside its own iframe, which our stylesheet and our custom
// properties never reach: the only way in is this object, every value already resolved, and the
// typeface handed over as a stylesheet the frame loads for itself.

// What this object can and cannot do, so nobody spends an afternoon rediscovering it: the channel
// is an allowlist, and it carries appearance only - colour, type, border, radius, spacing. Every
// property that would place one element against another is refused, and the frame says so in the
// console rather than failing quietly. Refused on the classes that exist: `height`, `minHeight`,
// `whiteSpace`, `alignSelf`, `display`, `overflow`, `textOverflow` on `.Label`, `height` on
// `.Input`, `alignItems` on `.Block`. Refused as classes outright: `.Field`, `.Grid`, `.GridCell`,
// `.CardForm`, `.Fieldset`, `.FieldsetCell`. So the frame lays its own fields out, and a label long
// enough to wrap drops the field beneath it out of line with the one beside it. Nothing here can
// correct that, and the labels are written in the reader's language, so which languages show it is
// not ours to decide either.

import type { Appearance, StripeElementsOptions } from '@stripe/stripe-js'

/** Token name (as emitted under the `tw` prefix) paired with the hex to fall back on. */
const PALETTE = {
  text: ['grey-50', '#2e3238'],
  label: ['grey-300', '#737d8c'],
  placeholder: ['grey-500', '#abb1ba'],
  border: ['grey-700', '#e3e5e8'],
  surface: ['grey-1000', '#ffffff'],
  disabled: ['grey-800', '#f1f2f4'],
  hover: ['aquamarine-400', '#5bbfc0'],
  focus: ['blue-100', '#29abe2'],
  danger: ['red-30', '#ea6983'],
  dangerText: ['red-50', '#e2294e']
} as const

type Swatch = keyof typeof PALETTE

/** Measured off the card-name input the fields sit under, so the two read as one form. */
const FIELD = {
  fontSize: '14px',
  lineHeight: '20px',
  borderRadius: '20px',
  padding: '5px 12px 5px 16px',
  borderWidth: '1px'
} as const

/**
 * Measured off the card-name label above the fields. `indent` is the field's own left padding:
 * the frame's runs of text sit flush with its boxes, so without it every label and message inside
 * the frame starts a step left of every one outside it.
 */
const LABEL = {
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: '400',
  indent: '16px',
  gap: '4px'
} as const

const FONT_FAMILY = 'Ubuntu, Calibri, Segoe, Helvetica, Arial, sans-serif'

const FOCUS_RING_ALPHA = '59'
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

const expand = (hex: string): string =>
  hex.length === 4
    ? `#${hex
        .slice(1)
        .split('')
        .map(digit => digit + digit)
        .join('')}`
    : hex

/**
 * Reads a UUI colour token from the document. Two traps: the theme is imported under the `tw`
 * prefix, so an unprefixed read returns the empty string; and the halo below needs a six-digit hex
 * to append its alpha to, so a token authored in any other notation has to fall back to its own
 * palette entry rather than reach the halo and turn into a colour from a different swatch.
 */
const swatch = (name: Swatch): string => {
  const [token, fallback] = PALETTE[name]
  if (typeof document === 'undefined') return fallback

  const value = getComputedStyle(document.documentElement).getPropertyValue(`--tw-color-${token}`).trim()

  return HEX.test(value) ? expand(value) : fallback
}

/** The halo is its own colour at 35%, an opacity the palette carries no token for. */
const ring = (colour: string): string => `0 0 0 3px ${colour}${FOCUS_RING_ALPHA}`

const build = (): Appearance => {
  const text = swatch('text')
  const label = swatch('label')
  const placeholder = swatch('placeholder')
  const border = swatch('border')
  const surface = swatch('surface')
  const disabled = swatch('disabled')
  const hover = swatch('hover')
  const focus = swatch('focus')
  const danger = swatch('danger')
  const dangerText = swatch('dangerText')

  const resting = {
    backgroundColor: surface,
    border: `${FIELD.borderWidth} solid ${border}`,
    borderRadius: FIELD.borderRadius,
    boxShadow: 'none',
    color: text,
    fontFamily: FONT_FAMILY,
    fontSize: FIELD.fontSize,
    lineHeight: FIELD.lineHeight,
    padding: FIELD.padding,
    transition: 'border-color .3s ease-in-out, box-shadow .3s ease-in-out'
  }

  return {
    variables: {
      borderRadius: FIELD.borderRadius,
      colorBackground: surface,
      colorDanger: danger,
      colorText: text,
      colorTextPlaceholder: placeholder,
      fontFamily: FONT_FAMILY,
      fontSizeBase: FIELD.fontSize
    },
    rules: {
      '.Input': resting,
      '.Input::placeholder': { color: placeholder },
      '.Input:hover': { borderColor: hover },
      '.Input:focus': { borderColor: focus, boxShadow: ring(focus) },
      '.Input:hover:focus': { borderColor: focus, boxShadow: ring(focus) },
      // A refused field keeps saying so under the pointer and under focus. Without the combined
      // selectors the plain hover and focus rules outrank `--invalid` on specificity alone, and the
      // field the form is rejecting paints as if it were fine.
      '.Input--invalid': { borderColor: danger, boxShadow: 'none', color: text },
      '.Input--invalid:hover': { borderColor: danger, boxShadow: 'none' },
      '.Input--invalid:focus': { borderColor: danger, boxShadow: ring(danger) },
      '.Input--invalid:hover:focus': { borderColor: danger, boxShadow: ring(danger) },
      '.Input:disabled': { backgroundColor: disabled, borderColor: disabled, color: placeholder },
      '.Label': {
        color: label,
        fontFamily: FONT_FAMILY,
        fontSize: LABEL.fontSize,
        fontWeight: LABEL.fontWeight,
        lineHeight: LABEL.lineHeight,
        marginBottom: LABEL.gap,
        marginLeft: LABEL.indent
      },
      '.Error': {
        color: dangerText,
        fontFamily: FONT_FAMILY,
        fontSize: LABEL.fontSize,
        fontWeight: LABEL.fontWeight,
        lineHeight: LABEL.lineHeight,
        marginLeft: LABEL.indent
      }
    }
  }
}

let cached: Appearance | null = null

/** Read once: the fields re-render on every keystroke in the card-name input, eight reads apiece. */
export const uuiAppearance = (): Appearance => (cached ??= build())

/** The frame has no access to the page's font, so it is handed the same request the page makes. */
export const uuiFonts: StripeElementsOptions['fonts'] = [
  {
    cssSrc:
      'https://fonts.googleapis.com/css?family=Ubuntu:300,300i,400,400i,500,500i,700,700i&subset=cyrillic,cyrillic-ext,greek,greek-ext,latin-ext'
  }
]
